import { useState } from "react";
import { X } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { galleryCategories, galleryItems } from "../mock";

function PageHero() {
  return (
    <section className="relative pt-44 pb-20 overflow-hidden">
      <div className="absolute inset-0">
        <img src="/images/epoxy.jpg" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/90 to-zinc-950/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 to-transparent" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6">
        <span className="text-orange-500 font-semibold text-sm tracking-[0.2em] uppercase">Our Work</span>
        <h1 className="mt-3 font-display font-black text-white text-4xl md:text-6xl tracking-tight">Project Gallery</h1>
        <p className="mt-4 text-zinc-300 text-lg max-w-2xl">A look at the facility floors we've transformed — warehouses, plants, arenas and distribution centers across Western New York.</p>
      </div>
    </section>
  );
}

function CategoryFilter({ categories, active, onSelect }) {
  return (
    <div className="flex flex-wrap gap-2 mb-10">
      {categories.map((cat) => (
        <button
          key={cat}
          onClick={() => onSelect(cat)}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
            active === cat
              ? "bg-orange-500 text-white"
              : "bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800"
          }`}
        >
          {cat}
        </button>
      ))}
    </div>
  );
}

function GalleryGrid({ items, onSelect }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {items.map((item, i) => (
        <Reveal key={item.id} delay={(i % 3) * 80}>
          <button
            onClick={() => onSelect(item)}
            className="group relative w-full rounded-xl overflow-hidden border border-zinc-800 block text-left"
          >
            <div className="h-64 overflow-hidden">
              <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/10 to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />
            <div className="absolute bottom-0 left-0 p-5">
              <span className="text-orange-400 text-xs font-semibold tracking-wide uppercase">{item.category}</span>
              <h3 className="font-display font-bold text-white text-lg">{item.title}</h3>
            </div>
          </button>
        </Reveal>
      ))}
    </div>
  );
}

function Lightbox({ item, onClose }) {
  if (!item) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={onClose}>
      <button className="absolute top-6 right-6 text-white/80 hover:text-white" onClick={onClose}>
        <X className="w-8 h-8" />
      </button>
      <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
        <img src={item.image} alt={item.title} className="w-full max-h-[80vh] object-contain rounded-lg" />
        <div className="mt-4 text-center">
          <span className="text-orange-400 text-sm font-semibold uppercase tracking-wide">{item.category}</span>
          <h3 className="font-display font-bold text-white text-xl">{item.title}</h3>
        </div>
      </div>
    </div>
  );
}

export default function Gallery() {
  const [active, setActive] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const items = active === "All" ? galleryItems : galleryItems.filter((i) => i.category === active);

  return (
    <>
      <PageHero />
      <section className="py-16 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6">
          <CategoryFilter categories={galleryCategories} active={active} onSelect={setActive} />
          <GalleryGrid items={items} onSelect={setLightbox} />
        </div>
      </section>
      <Lightbox item={lightbox} onClose={() => setLightbox(null)} />
    </>
  );
}
